/**
 * When a page change should start the new page at the top (Packet CX-4).
 *
 * Bobbie, on a family class card in /family/classes: "when you click on this
 * card it opens to the bottom of the class page instead of the top."
 *
 * THE FAULT. The app had no scroll reset on a route change: a client-side
 * navigation keeps the window's scroll offset, so the new page opened wherever
 * the old one was scrolled. Worse than a stale offset, the document is briefly
 * short while the old page is swapped out, the browser clamps the offset into
 * it, and Chrome's scroll anchoring then pins the page FOOTER (it is the
 * anchor node while the content above it is still empty) and pushes the offset
 * down by exactly the height of every row that loads in above it. Measured on
 * the real app: a class list scrolled to 2219 opened the class page at 8003
 * and settled at 8578, the very bottom. Chrome does not anchor at offset zero,
 * so landing on zero before the content arrives also stops the drift.
 *
 * THE RULE, kept here as a pure function so a test can pin every branch:
 *
 *   - Reset only when the PATHNAME changed. A ?tab=, ?q= or ?d= change is the
 *     same page and must not jump.
 *   - Never on a hash. A link with #class-post-<id> (or #task-, #request-,
 *     #board-...) names a place on the page, and the page scrolls there itself.
 *   - Never on POP. Back and Forward do not reset. The browser restores the
 *     offset it saved (history.scrollRestoration stays 'auto'; nothing in the
 *     app sets it to 'manual'), and lib/scrollRestore.ts puts a list back
 *     itself when the page returned from was short or still loading, the one
 *     case where the browser's own restore fails once the reset has left the
 *     window at 0.
 *   - Never when the navigation carries { keepScroll: true } in its state. No
 *     page needs that today; the flag exists so the first one that does can
 *     say so out loud instead of fighting this reset.
 *   - Never on the first page of the visit (nothing came before it).
 *
 * KNOWN EDGE, MEASURED. A link whose hash names something that no longer exists
 * (a #class-post- link to a deleted post) gets no reset, because the rule above
 * says never reset on a hash, so the page opens on the old offset and drifts to
 * the bottom exactly as it did before this change (a class page reached from a
 * notifications page scrolled to 3966: 83, 336, 498, then 8003). It is rare and
 * it is the packet's own rule working as written. The one-line fix is to delete
 * the hash check in shouldResetScrollOnRoute: RouteScrollReset runs in a layout
 * effect, ahead of every page's own scroll effect, so a page that does find its
 * anchor would still win. That change is safe only now that Back puts a list
 * back by itself (fix round 1); before that, the refute round proved that
 * removing it spread the Back regression to hash links. Left as written until
 * Bobbie or the PM says so.
 *
 * Plain strings, not react-router types, so node --test can import this file
 * without the router.
 */

export type RouteNavigationType = 'POP' | 'PUSH' | 'REPLACE';

/** The key a navigation puts in `state` to ask for its scroll to be left alone. */
export const KEEP_SCROLL_STATE_KEY = 'keepScroll';

/**
 * `navigate(to, { state: keepScrollState() })`: an explicit opt-out for the
 * rare page change that must not move the window. Merges into any state the
 * caller already passes.
 */
export function keepScrollState(extra?: Record<string, unknown>): Record<string, unknown> {
  return { ...(extra ?? {}), [KEEP_SCROLL_STATE_KEY]: true };
}

/** True when a navigation's state carries the keepScroll opt-out. */
export function wantsKeepScroll(state: unknown): boolean {
  return typeof state === 'object' && state !== null
    && (state as Record<string, unknown>)[KEEP_SCROLL_STATE_KEY] === true;
}

export interface RouteScrollInput {
  /** The pathname the previous page settled on, or null for the first page of the visit. */
  fromPathname: string | null;
  /** The pathname now on screen. */
  toPathname: string;
  /** location.hash of the new location, with or without the leading #. */
  hash: string;
  navigationType: RouteNavigationType;
  /** location.state of the new location. */
  state: unknown;
}

export function shouldResetScrollOnRoute(input: RouteScrollInput): boolean {
  if (input.fromPathname === null) return false;
  if (input.fromPathname === input.toPathname) return false;
  if (input.navigationType === 'POP') return false;
  if (input.hash.replace(/^#/, '') !== '') return false;
  if (wantsKeepScroll(input.state)) return false;
  return true;
}

/**
 * The field a #anchor entry's history state carries so it can be told apart
 * from every other #anchor entry (follow-up to #1299, item 2).
 */
export const FRAGMENT_ENTRY_STATE_KEY = 'scrollEntry';

/**
 * The key the scroll memory files a history entry under (fix round 3, R3-1;
 * follow-up to #1299, item 2).
 *
 * react-router gives an entry its own random location.key, EXCEPT two kinds that
 * both get the literal 'default': the first entry of a visit (its history state
 * is { idx: 0 }) and every entry a plain <a href="#anchor"> makes (the browser
 * makes the entry, react-router never sees it, so its state is null). Filing
 * both under 'default' made them one memory entry: on My classes at 2223, My
 * calendar, tap the "Important dates" pill, Back, Back, the list came back at
 * 4065 (the calendar's offset had refreshed the shared entry) where hub-staging
 * restores 2223. Family-reachable plain anchors: that pill (FamilyCalendar), the
 * FamilyDancerProfile stat boxes and the BagGroupDetail pills.
 *
 *   - a real key is used as it is;
 *   - 'default' with a numeric history idx is that visit entry: `default:<idx>`;
 *   - 'default' with a stamped entry id (stampFragmentEntry) is that one #anchor
 *     entry: `entry:<id>`. Two separate visits to the same anchor are two
 *     entries with two ids, so the second no longer restores the first's offset
 *     (measured on the real app at 390: My calendar, tap the pill, leave it at
 *     920, open a class; the menu, My calendar, the same pill, leave it at 2300,
 *     open a class; four Backs later the FIRST pill entry landed at 2300);
 *   - 'default' with neither (an entry nothing stamped, which should not happen
 *     in the app) is filed by its full path (`anchor:<path>?<search>#<hash>`),
 *     which at least keeps different anchors and different pages apart.
 */
export function memoryKeyFor(locationKey: string, historyState: unknown, fullPath: string): string {
  if (locationKey !== 'default') return locationKey;
  const state = typeof historyState === 'object' && historyState !== null
    ? (historyState as { idx?: unknown; [FRAGMENT_ENTRY_STATE_KEY]?: unknown })
    : undefined;
  const idx = state?.idx;
  if (typeof idx === 'number' && Number.isFinite(idx)) return `default:${idx}`;
  const entry = state?.[FRAGMENT_ENTRY_STATE_KEY];
  if (typeof entry === 'string' && entry !== '') return `entry:${entry}`;
  return `anchor:${fullPath}`;
}

/** The two things stampFragmentEntry needs of window.history. */
export interface StampableHistory {
  readonly state: unknown;
  replaceState(data: unknown, unused: string): void;
}

let stampCounter = 0;

/** A fresh id for one #anchor entry. The counter keeps two ids in one millisecond apart. */
export function newFragmentEntryId(): string {
  stampCounter += 1;
  return `${Date.now().toString(36)}-${stampCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Gives a #anchor entry its own identity (follow-up to #1299, item 2).
 *
 * A plain <a href="#anchor"> makes a history entry with state null, and the
 * browser then fires popstate with state null (measured in Chromium). Nothing
 * else tells that entry apart from another one on the same URL, so the scroll
 * memory could only file it by its path. This runs on that popstate and
 * replaces the entry's state, in place and with no URL argument, so the URL and
 * the entry stay exactly as they were, with an id memoryKeyFor files it under.
 *
 * `idx: null` is deliberate: react-router reads state.idx on every pop and push
 * (`getIndex() + 1`), and it falls back to { idx: null } for a null state. A
 * state without the field would read undefined and turn the next push's index
 * into NaN. Keeping null keeps router behaviour identical to the null state.
 *
 * Only a null or undefined state is stamped. An entry react-router made, the
 * first entry of a visit and an entry a photo viewer pushed all carry a state
 * object already, and are left alone, as is an entry already stamped.
 * Returns true when it stamped.
 */
export function stampFragmentEntry(
  history: StampableHistory,
  makeId: () => string = newFragmentEntryId,
): boolean {
  if (history.state !== null && history.state !== undefined) return false;
  history.replaceState({ idx: null, [FRAGMENT_ENTRY_STATE_KEY]: makeId() }, '');
  return true;
}

/** The browser surface installFragmentEntryStamp listens on. */
export interface StampTarget {
  readonly history: StampableHistory;
  addEventListener(type: 'popstate', listener: () => void): void;
  removeEventListener(type: 'popstate', listener: () => void): void;
}

/**
 * Stamps every #anchor entry as the browser creates it. MUST be installed
 * BEFORE the router mounts: react-router adds its own popstate listener when
 * BrowserRouter first commits, and listeners on one target run in the order
 * they were added, so ours (added when App.tsx is first evaluated) runs ahead
 * of it and the router's first render of the new entry already sees the stamp.
 * A stamp that landed after that render would change the entry's memory key
 * halfway through its life. Returns the function that removes the listener.
 */
export function installFragmentEntryStamp(target: StampTarget): () => void {
  const onPopState = (): void => { stampFragmentEntry(target.history); };
  target.addEventListener('popstate', onPopState);
  return () => target.removeEventListener('popstate', onPopState);
}
